/**
 * App-wide form kit: TanStack Form bound to design-system components.
 *
 *   const form = useAppForm({ defaultValues, validators: { onChange: zodSchema }, onSubmit });
 *   <form.AppField name="tone">{(f) => <f.SelectField label={t("settings:tone")} options={...} />}</form.AppField>
 *   <form.AppForm><form.SubmitActions onCancel={...} /></form.AppForm>
 *
 * Fields are controlled (the design system's inputs don't forward refs), show
 * errors through the design system's FormField after the field is touched or
 * a submit is attempted, and the zod schemas generated from the backend's
 * OpenAPI (`schemas` from "@/api") can be used directly as validators.
 */

import { FormField, Input, RadioGroup, type RadioOption, Select, type SelectOption, Textarea, Toggle } from "@design";
import { createFormHook, createFormHookContexts, useStore } from "@tanstack/react-form";
import { useId } from "react";
import { SaveBar } from "@/components/build/SaveBar";
import { i18n } from "@/i18n/i18n";

const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

/** First validation error, shown once the user has interacted or tried to submit. */
function useFieldError(): string | undefined {
  const field = useFieldContext<unknown>();
  // Field components only have field context (form context is for formComponents),
  // so read submit attempts from the field's own form store, reactively.
  const submitted = useStore(field.form.store, (s) => s.submissionAttempts > 0);
  const { isTouched, errors } = field.state.meta;
  if (!(isTouched || submitted) || errors.length === 0) return undefined;
  const first = errors[0] as unknown;
  if (typeof first === "string") return first;
  if (first && typeof first === "object" && "message" in first) return String(first.message);
  return i18n.t("errors:form.invalid");
}

interface BaseFieldProps {
  label: string;
  required?: boolean;
  /** Guidance under the label on what to enter. */
  hint?: string;
}

function TextField({
  label,
  required,
  hint,
  placeholder,
  maxLength,
}: Readonly<BaseFieldProps & { placeholder?: string; maxLength?: number }>) {
  const field = useFieldContext<string | null | undefined>();
  const id = useId();
  return (
    <FormField label={label} htmlFor={id} required={required} hint={hint} error={useFieldError()}>
      <Input
        id={id}
        name={field.name}
        value={field.state.value ?? ""}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
      />
    </FormField>
  );
}

/** A secret (key, token): masked, never autofilled. */
function SecretField({ label, required }: Readonly<BaseFieldProps>) {
  const field = useFieldContext<string>();
  const id = useId();
  return (
    <FormField label={label} htmlFor={id} required={required} error={useFieldError()}>
      <Input
        id={id}
        name={field.name}
        type="password"
        autoComplete="off"
        value={field.state.value ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
      />
    </FormField>
  );
}

function TextareaField({
  label,
  required,
  hint,
  placeholder,
  rows = 4,
}: Readonly<BaseFieldProps & { placeholder?: string; rows?: number }>) {
  const field = useFieldContext<string | null | undefined>();
  const id = useId();
  return (
    <FormField label={label} htmlFor={id} required={required} hint={hint} error={useFieldError()}>
      <Textarea
        id={id}
        name={field.name}
        rows={rows}
        value={field.state.value ?? ""}
        placeholder={placeholder}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
      />
    </FormField>
  );
}

function SelectField({
  label,
  required,
  options,
  placeholder,
}: Readonly<BaseFieldProps & { options: SelectOption[]; placeholder?: string }>) {
  const field = useFieldContext<string | null | undefined>();
  const id = useId();
  return (
    <FormField label={label} htmlFor={id} required={required} error={useFieldError()}>
      <Select
        id={id}
        name={field.name}
        aria-label={label}
        options={options}
        placeholder={placeholder}
        value={field.state.value ?? undefined}
        onChange={(value) => {
          field.handleChange(value);
          field.handleBlur();
        }}
      />
    </FormField>
  );
}

/** One of a few choices, as radio buttons under a label (styled like FormField's) that names the group. */
function RadioGroupField({ label, options }: Readonly<{ label: string; options: RadioOption[] }>) {
  const field = useFieldContext<string>();
  const labelId = useId();
  return (
    <div>
      <span id={labelId} className="mb-1.5 block text-sm font-medium text-content-secondary">
        {label}
      </span>
      <RadioGroup
        aria-labelledby={labelId}
        options={options}
        value={field.state.value}
        onChange={(value) => field.handleChange(value)}
      />
    </div>
  );
}

function ToggleField({ label }: Readonly<{ label: string }>) {
  const field = useFieldContext<boolean | null | undefined>();
  return (
    <Toggle label={label} checked={Boolean(field.state.value)} onChange={(checked) => field.handleChange(checked)} />
  );
}

/** Save/Cancel bar wired to form state: disabled until dirty, spinner while
 * submitting. Uses the shared SaveBar so every editable tab (forms and the
 * capabilities tab) has the save action in the same place with the same look. */
function SubmitActions({
  onCancel,
  submitLabel,
  requireChanges = true,
}: Readonly<{ onCancel: () => void; submitLabel?: string; requireChanges?: boolean }>) {
  const form = useFormContext();
  return (
    <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty] as const}>
      {([isSubmitting, isDirty]) => (
        <SaveBar
          dirty={isDirty}
          saving={isSubmitting}
          onSave={() => form.handleSubmit()}
          onCancel={onCancel}
          saveLabel={submitLabel}
          requireChanges={requireChanges}
        />
      )}
    </form.Subscribe>
  );
}

export const { useAppForm, withForm, withFieldGroup } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: { TextField, SecretField, TextareaField, SelectField, RadioGroupField, ToggleField },
  formComponents: { SubmitActions },
});
